import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarClock, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { api } from "../lib/api";

const thisMonth = () => new Date().toISOString().slice(0, 7);

export const AdvanceDeductDialog = ({ onDone }) => {
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(thisMonth());
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !month) return;
    setPlan(null);
    api.advanceDeductPreview(month).then(setPlan).catch((e) => toast.error(e?.response?.data?.detail || "تعذّر تحميل المعاينة"));
  }, [open, month]);

  const apply = async () => {
    setBusy(true);
    try {
      const r = await api.advanceDeductMonth(month);
      toast.success(`تم خصم ${r.total} من رواتب ${r.employees} موظف${r.salaries_updated ? ` وتحديث ${r.salaries_updated} سجل راتب` : ""}`);
      setOpen(false); onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر تنفيذ الخصم"); }
    finally { setBusy(false); }
  };
  const pending = (plan?.items || []).filter((i) => i.deduct > 0);

  return (
    <>
      <Button variant="outline" size="sm" className="gap-1.5 h-9 text-violet-700 border-violet-200" data-testid="advance-deduct-open" onClick={() => setOpen(true)}>
        <CalendarClock className="h-4 w-4" /> خصم أقساط الشهر
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg font-body max-h-[85vh] overflow-y-auto" dir="rtl" data-testid="advance-deduct-dialog">
          <DialogHeader>
            <DialogTitle>خصم أقساط السلف من الرواتب</DialogTitle>
            <DialogDescription>يُخصم القسط الشهري لكل سلفة مرة واحدة فقط في الشهر، ويُضاف لبند «السلف» في راتب الموظف لنفس الشهر إن وُجد.</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-600">الشهر</span>
            <Input type="month" dir="ltr" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44" data-testid="advance-deduct-month" />
          </div>
          {!plan ? (
            <div className="py-8 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" /></div>
          ) : plan.items.length === 0 ? (
            <p className="py-6 text-center text-sm text-slate-400" data-testid="advance-deduct-empty">لا توجد سلف بقسط شهري محدد</p>
          ) : (
            <div className="rounded-xl border border-slate-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500"><tr>
                  <th className="px-3 py-2 text-right">الموظف</th><th className="px-2 py-2">المتبقي</th><th className="px-2 py-2">الخصم</th></tr></thead>
                <tbody>
                  {plan.items.map((i) => (
                    <tr key={i.advance_id} className="border-t border-slate-100" data-testid={`advance-deduct-row-${i.advance_id}`}>
                      <td className="px-3 py-2 text-right"><div className="font-semibold text-slate-700">{i.employee_name}</div><div className="text-[11px] text-slate-400">{i.branch || "—"}</div></td>
                      <td className="px-2 py-2 text-center tabular-nums">{i.outstanding}</td>
                      <td className="px-2 py-2 text-center tabular-nums">
                        {i.already_deducted ? <span className="text-xs text-emerald-600">خُصم مسبقاً</span> : <b className="text-violet-700">{i.deduct}</b>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex justify-between bg-violet-50 px-3 py-2 text-sm font-semibold text-violet-800" data-testid="advance-deduct-total">
                <span>الإجمالي ({pending.length})</span><span className="tabular-nums">{plan.total}</span>
              </div>
            </div>
          )}
          <DialogFooter className="flex-row-reverse gap-2 sm:justify-start">
            <Button data-testid="advance-deduct-apply" disabled={busy || !pending.length} onClick={apply} className="bg-violet-600 hover:bg-violet-700 gap-1.5">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} تنفيذ الخصم
            </Button>
            <Button variant="outline" onClick={() => setOpen(false)}>إلغاء</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
